import { useTranslation } from 'react-i18next';
import { useMemo, useState, Fragment, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { Routes } from 'config';
import { useAppSelector } from 'store';
import { useIsDesktopDown } from 'hooks';
import { useCourseContext } from 'context';
import { selectIsGuestUser } from 'store/slices/auth';
import { BurgerButton } from 'components/burger-button';

import { Button } from '../button';

import { StepButton } from './step-button';
import { UserDropdown } from './user-dropdown';
import { ResponsiveMenu } from './responsive-menu';
import { LanguageButton } from './language-button';
import { NavigationLink } from './navigation-link';
import { NavLinkItem, TopNavigationProps } from './types';

const baseLinks: NavLinkItem[] = [
  {
    label: 'Dashboard',
    i18nKey: 'dashboard',
    href: Routes.Dashboard.Home,
  },
];

const userLinks = [
  {
    label: 'Manage Account',
    i18nKey: 'manageAccount',
    href: Routes.Profile.Home,
  },
];

export const TopNavigation = ({ logoUrl }: TopNavigationProps) => {
  const { isFinished, internalSteps } = useCourseContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isGuestUser = useAppSelector(selectIsGuestUser);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isDesktopDown = useIsDesktopDown();
  const { t } = useTranslation(['header', 'common']);

  const links = useMemo<NavLinkItem[]>(() => {
    if (!isFinished) {
      return baseLinks;
    }

    return [
      ...baseLinks,
      {
        label: 'Get Started',
        i18nKey: 'common:getStarted',
        href: Routes.GetStarted.Home,
      },
    ];
  }, [isFinished]);

  useEffect(() => {
    if (!isDesktopDown) {
      setIsMobileMenuOpen(false);
    }
  }, [isDesktopDown]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const showGetStartedHeader =
    pathname === Routes.GetStarted.Home && isFinished;

  const headerClassName = 'relative z-5 flex h-12 w-full items-center md:h-16';

  const logo = logoUrl && (
    <div className="mt-1.5 h-6 md:h-10">
      <Link to={Routes.Dashboard.Home}>
        <img
          alt="logo"
          src={logoUrl}
          className="h-full w-auto object-contain"
        />
      </Link>
    </div>
  );

  if (showGetStartedHeader) {
    return (
      <div className={`${headerClassName} justify-between`}>
        {logo}
        <Button
          component={Link}
          variant="outlined"
          className="ml-auto"
          to={Routes.Dashboard.Home}
        >
          {t('backToOnboarding')}
        </Button>
        <UserDropdown showLogoutButton links={userLinks} />
      </div>
    );
  }

  return (
    <>
      <div className={headerClassName}>
        <div className="flex shrink-0 items-center">
          {logo}
          <div className="hidden flex-1 pl-8 xl:flex">
            {links.map((link) => (
              <NavigationLink
                key={link.label}
                href={link.href}
                variant="desktop"
                label={t(link.i18nKey)}
                external={link.external}
                asButton={link.asButton}
                onClick={handleLinkClick}
                isActive={pathname === link.href}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-1 items-center">
          <div className="flex flex-1 items-center justify-center">
            {internalSteps?.map((item, index) => (
              <Fragment key={item.presentStep.id}>
                {index !== 0 && (
                  <div className="bg-white-primary h-px min-w-2 md:min-w-12.5" />
                )}
                <StepButton
                  item={item.presentStep}
                  onClick={() => {
                    const { id, resourceVersions } = item.presentStep;
                    const isResource =
                      resourceVersions && resourceVersions.length > 0;
                    if (isResource) {
                      navigate(Routes.Course.Resource.replace(':id', id));
                    } else {
                      navigate(Routes.Course.Form.replace(':id', id));
                    }
                  }}
                />
              </Fragment>
            ))}
          </div>
          <LanguageButton className="ml-6 hidden xl:flex" />
          <UserDropdown
            showLogoutButton
            links={userLinks}
            className="hidden xl:flex"
          />
          <BurgerButton
            isOpen={isMobileMenuOpen}
            className="ml-3 xl:hidden"
            onClick={handleMobileMenuToggle}
          />
        </div>
      </div>
      {isMobileMenuOpen && (
        <ResponsiveMenu
          links={links}
          pathname={pathname}
          userLinks={userLinks}
          isGuestUser={isGuestUser}
          onClick={handleLinkClick}
        />
      )}
    </>
  );
};
